import {
  Link,
  useCanGoBack,
  useNavigate,
  useRouter,
} from '@tanstack/react-router';
import { format } from 'date-fns';
import { CircleArrowLeft, Loader2 } from 'lucide-react';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { useCreateEventMutation } from '~entities/events';
import { useGetSessionById } from '~entities/session/api/get-session-by-id';
import { useGetStudioDetails } from '~entities/studios/api/get-studio-details';

import {
  calculateDurationInMinutes,
  formatDateTime,
  isValidTimeRange,
  normalizeDate,
  normalizeTime,
} from '~features/classes/ui/helpers';
import { buildEventFormAutoHideHint } from '~features/events/events.helpers';
import {
  DEFAULT_EVENT_AUTO_HIDE_GRACE_HOURS,
  EVENT_TIME_CONFIG,
  EventAutoHideGraceHours,
} from '~features/events/events.types';
import { EventPortalVisibilityFields } from '~features/events/ui/event-portal-visibility-fields';
import { PublicEventsLinkField } from '~features/events/ui/public-events-link-field';
import { CoverImageSelector } from '~features/shared/cover-image-selector';
import { LocationSelector } from '~features/shared/location-selector';
import { PolicySelector } from '~features/shared/policy-selector';
import { TimePickerDropdown } from '~features/shared/time-picker-dropdown';

import { DateTimePicker } from '~shared/components/date-time-picker';
import { MAX_AGE_YEARS } from '~shared/constants/age';
import { colorOptions } from '~shared/constants/colors';
import type { Days } from '~shared/constants/days';
import { TuitionBillingCycle } from '~shared/constants/tuition-billing-cycle';
import { Button } from '~shared/shadcn/ui/button';
import { Card, CardContent } from '~shared/shadcn/ui/card';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import { Switch } from '~shared/shadcn/ui/switch';

import { GroupsSelector } from '../../shared/groups-selector';
import { InstructorSelector } from '../../shared/instructor-selector';
import { RoomSelector } from '../../shared/room-selector';
import { SessionSelector } from '../../shared/session-selector';
import { TagsSelector } from '../../shared/tags-selector';
import { EventDateSelectionSwitch } from './event-date-selection-switch';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

interface FormData {
  eventInfo: {
    eventName: string;
    location: {
      label: string | null;
      value: string | null;
    };
    room: {
      label: string | null;
      value: string | null;
    };
    instructor: Array<{
      label: string;
      value: string;
    }>;
    seats: number | null;
    minAge: {
      year: number;
      month: number;
    };
    maxAge: {
      year: number;
      month: number;
    };
    session: {
      label: string;
      value: string;
    };
    hide: boolean;
    autoHideAfterEnd: boolean;
    autoHideGraceHours: number;
    includeInPublicSchedule: boolean;
    allowSpecificDatesSelection: boolean;
    color: string;
  };
  days: Array<string>;
  dates: {
    startDate: string;
    endDate: string;
    registrationStartDate: string;
  };
  times: {
    startTime: string;
    endTime: string;
    duration: number;
  };
  otherInfo: {
    registrationFee: string;
    tags: Array<{
      label: string;
      value: string;
    }>;
    policyGroup: Array<{
      label: string;
      value: string;
    }>;
    group: Array<{
      label: string;
      value: string;
    }>;
    description: string;
    tuitionBillingCycle: string;
    costumeFee: string;
    tuitionFee: string;
    billingDay: number;
    billingFrequencyDays?: number;
    prorateTuition: boolean;
  };
  timeConfig: 'same' | 'different';
  dayTimes: Record<Days, { startTime: string; endTime: string }>;
}

export const CreateNewEventPage: React.FC = () => {
  const today = new Date();
  const router = useRouter();
  const canGoBack = useCanGoBack();

  const navigate = useNavigate({
    from: '/events/new',
  });
  const mutation = useCreateEventMutation();
  const { data: studioDetails } = useGetStudioDetails();
  const studioProrationEnabled = studioDetails?.prorationEnabled !== false;

  const [selectedFile, setSelectedFile] = useState<File | string | null>(null);
  const [selectedFileType, setSelectedFileType] = useState<'file' | 'url'>(
    'file',
  );

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      days: [],
      eventInfo: {
        eventName: '',
        location: { label: null, value: null },
        room: { label: null, value: null },
        instructor: [],
        seats: undefined,
        minAge: undefined,
        maxAge: undefined,
        // session: { label: '', value: '' },
        hide: false,
        autoHideAfterEnd: true,
        autoHideGraceHours: DEFAULT_EVENT_AUTO_HIDE_GRACE_HOURS,
        includeInPublicSchedule: false,
        allowSpecificDatesSelection: false,
        color: '#F5F5F5',
      },
      dates: {
        startDate: '',
        endDate: '',
        registrationStartDate: '',
      },
      times: {
        startTime: format(today, 'HH:mm'),
        endTime: format(today, 'HH:mm'),
        duration: 0,
      },
      otherInfo: {
        registrationFee: '',
        costumeFee: '',
        tags: [],
        policyGroup: [],
        group: [],
        description: '',
        tuitionBillingCycle: TuitionBillingCycle.ONE_TIME,
        tuitionFee: '',
        billingDay: 1,
        prorateTuition: true,
      },
      timeConfig: 'same',
      dayTimes: DAYS.reduce(
        (acc, day) => ({
          ...acc,
          [day]: {
            startTime: '12:00',
            endTime: '12:00',
          },
        }),
        {},
      ),
    },
    mode: 'onChange',
  });

  const startTime = watch('times.startTime');
  const endTime = watch('times.endTime');

  const sessionId = watch('eventInfo.session.value');

  const { data: sessionData } = useGetSessionById(sessionId, {
    enabled: !!sessionId,
  });

  useEffect(() => {
    if (startTime && endTime) {
      setValue(
        'times.duration',
        calculateDurationInMinutes(startTime, endTime),
      );
    }
  }, [startTime, endTime, setValue]);

  useEffect(() => {
    // Register days field with validation
    register('days', {
      validate: (value) => value.length > 0 || 'Please select at least one day',
    });
  }, [register]);

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    if (!selectedFile) {
      toast.error('Please select a cover image');
      return;
    }

    if (data.days.length === 0) {
      toast.error('Please select at least one day');
      return;
    }

    const startDate = normalizeDate(data.dates.startDate);
    const endDate = normalizeDate(data.dates.endDate);

    const startTime = normalizeTime(data.times.startTime);
    const endTime = normalizeTime(data.times.endTime);

    // format time to HH:MM AM/PM using date-fns

    // Generate formatted date-time values
    const formattedStartDateTime = formatDateTime(startDate, startTime);
    const formattedEndDateTime = formatDateTime(startDate, endTime);

    // Calculate availability
    const availability =
      data.timeConfig === 'same'
        ? data.days.map((day) => ({
            day,
            startTime: formattedStartDateTime,
            endTime: formattedEndDateTime,
          }))
        : data.days.map((day) => ({
            day,
            startTime: formatDateTime(
              startDate,
              normalizeTime(data.dayTimes[day as Days].startTime || startTime),
            ),
            endTime: formatDateTime(
              startDate,
              normalizeTime(data.dayTimes[day as Days].endTime || endTime),
            ),
          }));

    try {
      await mutation.mutateAsync({
        file: selectedFile,
        fileType: selectedFileType,
        eventDetails: {
          // Event Info
          title: data.eventInfo.eventName,
          hide: data.eventInfo.hide,
          autoHideAfterEnd: data.eventInfo.autoHideAfterEnd,
          autoHideGraceHours: data.eventInfo.autoHideGraceHours,
          includeInPublicSchedule: data.eventInfo.includeInPublicSchedule,
          allowSpecificDatesSelection:
            data.eventInfo.allowSpecificDatesSelection,
          color: data.eventInfo.color,
          location: data.eventInfo.location.value ?? null,
          room: data.eventInfo.room.value ?? null,
          instructor: data.eventInfo.instructor.map(
            (instructor) => instructor.value,
          ),
          maxSize: Number(data.eventInfo.seats),
          startYear: data.eventInfo.minAge.year,
          startMonth: data.eventInfo.minAge.month,
          endYear: data.eventInfo.maxAge.year,
          endMonth: data.eventInfo.maxAge.month,
          group: data.otherInfo.group.map((group) => group.value),
          tags: data.otherInfo.tags.map((tag) => tag.value),

          // Dates and Times
          startDate: startDate,
          endDate: endDate,
          startTime: startTime,
          endTime: endTime,
          duration: data.times.duration,
          registrationStartDate: data.dates.registrationStartDate,

          // Other Info
          description: data.otherInfo.description,
          policyGroup: data.otherInfo.policyGroup.map((policy) => policy.value),
          registrationFeeAmount: Number(data.otherInfo.registrationFee),
          days: data.days,
          oneTime:
            data.otherInfo.tuitionBillingCycle === TuitionBillingCycle.ONE_TIME,
          tuitionBillingCycle: data.otherInfo.tuitionBillingCycle,
          availability,
          timeConfig: data.timeConfig,
          session: data.eventInfo.session.value,
          costumeFee: Number(data.otherInfo.costumeFee),
          tuitionFee: Number(data.otherInfo.tuitionFee),
          prorateTuition: data.otherInfo.prorateTuition,
          ...(data.otherInfo.tuitionBillingCycle === 'monthly' && {
            billingDay: data.otherInfo.billingDay,
          }),
          ...(data.otherInfo.tuitionBillingCycle === TuitionBillingCycle.EVERY_X_DAYS && {
            billingFrequencyDays: data.otherInfo.billingFrequencyDays,
          }),
        },
      });
      // Show success toast
      toast.success('Event created successfully!');
      void navigate({
        to: '/events',
      });
    } catch (error) {
      console.error(
        '🚀 ~ const onSubmit:SubmitHandler<FormData>= ~ error:',
        error,
      );
      // Show error toast
      toast.error('Failed to create event. Please try again.');
    }
  };

  const handleFileSelect = useCallback((
    file: File | string | null,
    fileType: 'file' | 'url',
  ) => {
    setSelectedFile(file);
    setSelectedFileType(fileType);
  }, []);

  return (
    <div className='pb-56'>
      {canGoBack ? (
        <Button
          variant={'ghost'}
          className='flex items-center p-2 [&_svg]:size-6'
          disabled={!canGoBack}
          onClick={() => {
            router.history.back();
          }}
        >
          <CircleArrowLeft className='size-6' />
          <h2 className='text-2xl font-semibold'>Add Event</h2>
        </Button>
      ) : (
        <Link to='/events' className='flex items-center gap-2'>
          <CircleArrowLeft className='size-6' />
          <h2 className='text-2xl font-semibold'>Add Event</h2>
        </Link>
      )}

      <Card className='mx-auto mt-6 w-full max-w-4xl border border-border shadow-none'>
        <CoverImageSelector
          onFileSelect={handleFileSelect}
          selectDefaultOnLoad
        />
        {/* <CoverImageSelector type='event' /> */}
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className='space-y-6'>
            <div className='space-y-4'>
              <h2 className='text-xl font-semibold'>Event Info</h2>
              <div className='space-y-2'>
                <Label>Event Name</Label>
                <Input
                  {...register('eventInfo.eventName', {
                    required: 'Event name is required',
                  })}
                  placeholder='Event Name'
                />
                {errors.eventInfo?.eventName && (
                  <span className='text-sm text-destructive'>
                    {errors.eventInfo.eventName.message}
                  </span>
                )}
              </div>

              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-4'>
                  <div className='flex items-center space-x-2'>
                    <Label htmlFor='color'>Color</Label>
                    <Select
                      value={watch('eventInfo.color')}
                      onValueChange={(value) => {
                        setValue('eventInfo.color', value);
                      }}
                    >
                      <SelectTrigger className='w-[180px]'>
                        <SelectValue placeholder='Select a color'>
                          <div className='flex items-center space-x-2'>
                            {watch('eventInfo.color') && (
                              <div
                                className='size-4 rounded-full'
                                style={{
                                  backgroundColor: watch('eventInfo.color'),
                                }}
                              />
                            )}
                            <span>
                              {colorOptions.find(
                                (c) => c.value === watch('eventInfo.color'),
                              )?.label ?? 'Select a color'}
                            </span>
                          </div>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {colorOptions.map((color) => (
                          <SelectItem key={color.value} value={color.value}>
                            <div className='flex items-center space-x-2'>
                              <div
                                className='size-4 rounded-full'
                                style={{ backgroundColor: color.value }}
                              />
                              <span>{color.label}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <EventPortalVisibilityFields
                  hide={watch('eventInfo.hide')}
                  onHideChange={(checked) => {
                    setValue('eventInfo.hide', checked);
                  }}
                  hideLabel='Hidden'
                  autoHideAfterEnd={watch('eventInfo.autoHideAfterEnd')}
                  onAutoHideChange={(checked) => {
                    setValue('eventInfo.autoHideAfterEnd', checked);
                  }}
                  autoHideGraceHours={
                    watch(
                      'eventInfo.autoHideGraceHours',
                    ) as EventAutoHideGraceHours
                  }
                  onGraceChange={(hours) => {
                    setValue('eventInfo.autoHideGraceHours', hours);
                  }}
                  showGrace={
                    watch('eventInfo.autoHideAfterEnd') === true &&
                    Boolean(watch('dates.endDate'))
                  }
                  visibilityHint={buildEventFormAutoHideHint({
                    autoHideAfterEnd: watch('eventInfo.autoHideAfterEnd'),
                    autoHideGraceHours: watch('eventInfo.autoHideGraceHours'),
                    endDate: watch('dates.endDate'),
                    endTime: watch('times.endTime'),
                    timeConfig: watch('timeConfig'),
                    availability:
                      watch('timeConfig') === EVENT_TIME_CONFIG.DIFFERENT
                        ? (watch('days') ?? []).map((day) => ({
                            day,
                            endTime: watch('dayTimes')?.[day]?.endTime,
                          }))
                        : [],
                  })}
                />
              </div>

              <div className='space-y-4 rounded-lg border border-border p-4'>
                <h3 className='text-lg font-semibold'>
                  Parent portal &amp; public events
                </h3>
                <p className='rounded-md bg-blue-50 px-3 py-2 text-sm text-muted-foreground dark:bg-blue-950/30'>
                  IMPORTANT: Add the embed codes inside the section of your
                  website before the closing tag.
                </p>
                <PublicEventsLinkField />
                <div className='flex items-center justify-between gap-2'>
                  <Label
                    htmlFor='includeInPublicSchedule'
                    className='cursor-pointer'
                  >
                    Show on public events (no login link)
                  </Label>
                  <Switch
                    id='includeInPublicSchedule'
                    checked={watch('eventInfo.includeInPublicSchedule')}
                    onCheckedChange={(v) => {
                      setValue('eventInfo.includeInPublicSchedule', v);
                    }}
                  />
                </div>
              </div>

              <div className='space-y-2'>
                <Label>Location</Label>
                <Controller
                  control={control}
                  name='eventInfo.location'
                  render={({ field }) => (
                    <LocationSelector
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                {errors.eventInfo?.location && (
                  <span className='text-sm text-destructive'>
                    {errors.eventInfo.location.message}
                  </span>
                )}
              </div>

              <div className='space-y-2'>
                <Label>Room</Label>
                <Controller
                  control={control}
                  name='eventInfo.room'
                  render={({ field }) => (
                    <RoomSelector
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                {errors.eventInfo?.room && (
                  <span className='text-sm text-destructive'>
                    {errors.eventInfo.room.message}
                  </span>
                )}
              </div>

              <div className='space-y-2'>
                <Label>Number of Seats</Label>
                <Input
                  type='number'
                  {...register('eventInfo.seats', {
                    required: 'Number of seats is required',
                    min: { value: 1, message: 'Must have at least 1 seat' },
                    valueAsNumber: true,
                  })}
                  placeholder='Number of Seats'
                />
                {errors.eventInfo?.seats && (
                  <span className='text-sm text-destructive'>
                    {errors.eventInfo.seats.message}
                  </span>
                )}
              </div>

              <div className='flex gap-4'>
                <div className='flex-1 space-y-2'>
                  <Label>Min Age</Label>
                  <div className='flex gap-4'>
                    <div>
                      <Input
                        type='number'
                        {...register('eventInfo.minAge.year', {
                          required: 'Enter the minimum age year.',
                          min: {
                            value: 0,
                            message: 'Minimum age year must be at least 1.',
                          },
                          max: {
                            value: MAX_AGE_YEARS,
                            message: `Minimum age year must be at most ${MAX_AGE_YEARS}.`,
                          },
                          valueAsNumber: true,
                        })}
                        placeholder='year'
                      />
                      {typeof errors.eventInfo?.minAge?.year?.message ===
                        'string' && (
                        <span className='text-sm text-destructive'>
                          {errors.eventInfo.minAge.year.message}
                        </span>
                      )}
                    </div>
                    <div>
                      <Input
                        type='number'
                        {...register('eventInfo.minAge.month', {
                          required: 'Enter the minimum age month.',
                          min: {
                            value: 0,
                            message: 'Minimum age month must be at least 0.',
                          },
                          max: {
                            value: 11,
                            message: 'Minimum age month must be at most 11.',
                          },
                          valueAsNumber: true,
                        })}
                        placeholder='month'
                      />
                      {typeof errors.eventInfo?.minAge?.month?.message ===
                        'string' && (
                        <span className='text-sm text-destructive'>
                          {errors.eventInfo.minAge.month.message}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className='flex-1 space-y-2'>
                  <Label>Max Age</Label>
                  <div className='flex gap-4'>
                    <div>
                      <Input
                        type='number'
                        {...register('eventInfo.maxAge.year', {
                          required: 'Enter the maximum age year.',
                          min: {
                            value: 1,
                            message: 'Maximum age year must be at least 1.',
                          },
                          max: {
                            value: MAX_AGE_YEARS,
                            message: `Maximum age year must be at most ${MAX_AGE_YEARS}.`,
                          },
                          validate: (value, formValues) =>
                            value >= formValues.eventInfo.minAge.year ||
                            'Maximum age year must be greater than or equal to minimum age year.',
                          valueAsNumber: true,
                        })}
                        placeholder='year'
                      />
                      {typeof errors.eventInfo?.maxAge?.year?.message ===
                        'string' && (
                        <span className='text-sm text-destructive'>
                          {errors.eventInfo.maxAge.year.message}
                        </span>
                      )}
                    </div>
                    <div>
                      <Input
                        type='number'
                        {...register('eventInfo.maxAge.month', {
                          required: 'Enter the maximum age month.',
                          min: {
                            value: 0,
                            message: 'Maximum age month must be at least 0.',
                          },
                          max: {
                            value: 11,
                            message: 'Maximum age month must be at most 11.',
                          },

                          valueAsNumber: true,
                        })}
                        placeholder='month'
                      />
                      {typeof errors.eventInfo?.maxAge?.month?.message ===
                        'string' && (
                        <span className='text-sm text-destructive'>
                          {errors.eventInfo.maxAge.month.message}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className='space-y-2'>
                <Label>Instructor</Label>
                <Controller
                  control={control}
                  name='eventInfo.instructor'
                  render={({ field }) => (
                    <InstructorSelector
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                {errors.eventInfo?.instructor && (
                  <span className='text-sm text-destructive'>
                    {errors.eventInfo.instructor.message}
                  </span>
                )}
              </div>

              <div className='space-y-2'>
                <Label>Session</Label>
                <Controller
                  control={control}
                  name='eventInfo.session'
                  rules={{
                    required: 'Session is required',
                    validate: (session, formValues) => {
                      if ((session && !session.value) || session === undefined)
                        return 'Session selection is required';

                      if (!formValues.dates.startDate) return true;

                      if (!sessionData) return true;

                      if (!('billingDate' in sessionData)) return true;

                      if (!sessionData.billingDate) return true;

                      const billingMoment = moment(
                        sessionData.billingDate,
                      ).startOf('day');
                      const classMoment = moment(
                        formValues.dates.startDate,
                      ).startOf('day');

                      const formattedBillingDate =
                        billingMoment.format('MMM D, YYYY');

                      return (
                        billingMoment.isSameOrBefore(classMoment) ||
                        `Session billing date (${formattedBillingDate}) cannot be after event start date`
                      );
                    },
                  }}
                  render={({ field }) => (
                    <SessionSelector
                      session={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                {errors.eventInfo?.session && (
                  <span className='text-sm text-destructive'>
                    {errors.eventInfo.session.message}
                  </span>
                )}
              </div>

              <div className='space-y-2'>
                <Label>Description</Label>
                <textarea
                  {...register('otherInfo.description')}
                  placeholder='Enter Event description'
                  className='h-24 w-full rounded-md border p-2'
                />
              </div>
            </div>

            <div className='space-y-4'>
              <h2 className='text-xl font-semibold'>Dates</h2>
              <div className='flex flex-col space-y-4'>
                <div className='flex space-x-4'>
                  <div className='flex-1 space-y-2'>
                    <Label>Start Date</Label>
                    <Controller
                      control={control}
                      name='dates.startDate'
                      rules={{
                        required: 'Start date is required',
                        validate: (value, formValues) => {
                          if (!value) return true;

                          // Check if we have a session selected
                          const sessionValue =
                            formValues.eventInfo.session.value;
                          if (!sessionValue) return true;

                          // Check if session data is available
                          if (!sessionData) return true;

                          // Type guard to ensure sessionData is a valid session object
                          if (!('billingDate' in sessionData)) return true;

                          // Check if billing date exists
                          if (!sessionData.billingDate) return true;

                          // Compare dates using moment
                          const billingMoment = moment(
                            sessionData.billingDate,
                          ).startOf('day');
                          const classMoment = moment(value).startOf('day');

                          const formattedBillingDate =
                            billingMoment.format('MMM D, YYYY');

                          return (
                            billingMoment.isSameOrBefore(classMoment) ||
                            `Event start date must be on or after session billing date (${formattedBillingDate})`
                          );
                        },
                      }}
                      render={({ field }) => (
                        <DateTimePicker
                          hourCycle={24}
                          granularity='day'
                          displayFormat={{ hour24: 'PPP' }}
                          value={
                            field.value
                              ? new Date(field.value + 'T00:00:00')
                              : undefined
                          }
                          onChange={(date) => {
                            field.onChange(
                              date ? format(date, 'yyyy-MM-dd') : undefined,
                            );
                          }}
                        />
                      )}
                    />
                    {errors.dates?.startDate && (
                      <span className='text-sm text-destructive'>
                        {errors.dates.startDate.message}
                      </span>
                    )}
                  </div>

                  <div className='flex-1 space-y-2'>
                    <Label>End Date</Label>
                    <Controller
                      control={control}
                      name='dates.endDate'
                      rules={{
                        required: 'End date is required',
                        validate: (value, formValues) => {
                          if (!value || !formValues.dates.startDate) return true;
                          return (
                            new Date(value + 'T00:00:00') >=
                              new Date(formValues.dates.startDate + 'T00:00:00') ||
                            'End date must be on or after start date'
                          );
                        },
                      }}
                      render={({ field }) => (
                        <DateTimePicker
                          hourCycle={24}
                          granularity='day'
                          displayFormat={{ hour24: 'PPP' }}
                          value={
                            field.value
                              ? new Date(field.value + 'T00:00:00')
                              : undefined
                          }
                          onChange={(date) => {
                            field.onChange(
                              date ? format(date, 'yyyy-MM-dd') : undefined,
                            );
                          }}
                        />
                      )}
                    />
                    {errors.dates?.endDate && (
                      <span className='text-sm text-destructive'>
                        {errors.dates.endDate.message}
                      </span>
                    )}
                  </div>
                </div>

                <div className='flex-1 space-y-2'>
                  <Label>Registration Start Date</Label>
                  <Controller
                    control={control}
                    name='dates.registrationStartDate'
                    rules={{
                      validate: (value, formValues) => {
                        if (!value) return true; // Not required
                        return (
                          new Date(value) <
                            new Date(formValues.dates.startDate) ||
                          'Registration start date must be before event start date'
                        );
                      },
                    }}
                    render={({ field }) => (
                      <DateTimePicker
                        hourCycle={24}
                        granularity='day'
                        displayFormat={{ hour24: 'PPP' }}
                        value={
                          field.value
                            ? new Date(field.value + 'T00:00:00')
                            : undefined
                        }
                        onChange={(date) => {
                          field.onChange(
                            date ? format(date, 'yyyy-MM-dd') : undefined,
                          );
                        }}
                      />
                    )}
                  />
                  {errors.dates?.registrationStartDate && (
                    <span className='text-sm text-destructive'>
                      {errors.dates.registrationStartDate.message}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {watch('otherInfo.tuitionBillingCycle') !==
            TuitionBillingCycle.ONE_TIME ? (
              <div className='space-y-4'>
                <h2 className='text-xl font-semibold'>Times</h2>
                <div className='flex items-center space-x-2'>
                  <Controller
                    control={control}
                    name='timeConfig'
                    render={({ field }) => (
                      <Switch
                        checked={field.value === 'different'}
                        onCheckedChange={(checked) => {
                          field.onChange(checked ? 'different' : 'same');
                        }}
                      />
                    )}
                  />
                  <Label>Different Times for Each Selected Day</Label>
                </div>
                <EventDateSelectionSwitch
                  checked={watch('eventInfo.allowSpecificDatesSelection')}
                  onCheckedChange={(checked) => {
                    setValue('eventInfo.allowSpecificDatesSelection', checked);
                  }}
                />

                <div className='space-y-4'>
                  {watch('timeConfig') === 'same' ? (
                    <>
                      <div className='space-y-4'>
                        <h2 className='text-xl font-semibold'>Days</h2>
                        <div className='flex flex-wrap gap-2'>
                          {DAYS.map((day) => (
                            <div
                              key={day}
                              className='flex items-center space-x-2'
                            >
                              <Checkbox
                                id={day}
                                checked={watch('days').includes(day)}
                                onCheckedChange={(checked) => {
                                  const currentDays = watch('days');
                                  if (checked) {
                                    setValue('days', [...currentDays, day], {
                                      shouldValidate: true,
                                    });
                                  } else {
                                    setValue(
                                      'days',
                                      currentDays.filter((d) => d !== day),
                                      { shouldValidate: true },
                                    );
                                  }
                                }}
                              />
                              <Label htmlFor={day}>{day}</Label>
                            </div>
                          ))}
                        </div>
                        {errors.days && (
                          <span className='mt-2 block text-sm font-medium text-destructive'>
                            {errors.days.message}
                          </span>
                        )}
                      </div>

                      <div className='flex flex-wrap gap-4'>
                        <div className='min-w-[min(100%,15.5rem)] flex-1 space-y-2'>
                          <Label>Start Time</Label>
                          <Controller
                            control={control}
                            name='times.startTime'
                            rules={{ required: 'Start time is required' }}
                            render={({ field }) => (
                              <TimePickerDropdown
                                value={field.value}
                                onChange={(date) => {
                                  field.onChange(date);
                                }}
                              />
                            )}
                          />
                          {errors.times?.startTime && (
                            <span className='text-sm text-destructive'>
                              {errors.times.startTime.message}
                            </span>
                          )}
                        </div>
                        <div className='min-w-[min(100%,15.5rem)] flex-1 space-y-2'>
                          <Label>End Time</Label>
                          <Controller
                            control={control}
                            name='times.endTime'
                            rules={{
                              required: 'End time is required',
                              validate: (value) => {
                                const currentStartTime =
                                  watch('times.startTime');
                                if (!currentStartTime || !value) return true;
                                return (
                                  isValidTimeRange(currentStartTime, value) ||
                                  'End time must be after start time'
                                );
                              },
                            }}
                            render={({ field }) => (
                              <TimePickerDropdown
                                value={field.value}
                                onChange={(date) => {
                                  field.onChange(date);
                                }}
                              />
                            )}
                          />
                          {errors.times?.endTime && (
                            <span className='text-sm text-destructive'>
                              {errors.times.endTime.message}
                            </span>
                          )}
                        </div>
                        <div className='min-w-[min(100%,15.5rem)] flex-1 space-y-2'>
                          <Label>Duration (minutes)</Label>
                          <Input
                            type='text'
                            {...register('times.duration')}
                            disabled
                            value={calculateDurationInMinutes(
                              startTime,
                              endTime,
                            )}
                            placeholder='MM'
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className='space-y-6'>
                      <div className='flex items-center gap-4'>
                        <div className='w-20'></div>
                        <div className='flex flex-1 space-x-4'>
                          <div className='flex-1 text-center'>
                            <Label>Start Time</Label>
                          </div>
                          <div className='flex w-8 items-center'></div>
                          <div className='flex-1 text-center'>
                            <Label>End Time</Label>
                          </div>
                        </div>
                      </div>

                      {DAYS.map((day, index) => (
                        <div key={day}>
                          <div className='flex items-center gap-4'>
                            <div className='flex h-10 items-center gap-4'>
                              <Checkbox
                                id={day}
                                checked={watch('days').includes(day)}
                                onCheckedChange={(checked) => {
                                  const currentDays = watch('days');
                                  if (checked) {
                                    setValue('days', [...currentDays, day], {
                                      shouldValidate: true,
                                    });
                                  } else {
                                    setValue(
                                      'days',
                                      currentDays.filter((d) => d !== day),
                                      { shouldValidate: true },
                                    );
                                  }
                                }}
                              />
                              <Label htmlFor={day} className='w-20'>
                                {day}
                              </Label>

                              <div className='flex flex-1 space-x-4'>
                                {watch('days').includes(day) ? (
                                  <>
                                    <div className='flex-1'>
                                      <Controller
                                        control={control}
                                        name={`dayTimes.${day}.startTime`}
                                        rules={{
                                          required: 'Start time is required',
                                        }}
                                        render={({ field }) => (
                                          <TimePickerDropdown
                                            value={field.value}
                                            onChange={(date) => {
                                              field.onChange(date);
                                            }}
                                          />
                                        )}
                                      />
                                    </div>
                                    <div className='flex w-8 items-center justify-center'></div>
                                    <div className='flex-1'>
                                      <Controller
                                        control={control}
                                        name={`dayTimes.${day}.endTime`}
                                        rules={{
                                          required: 'End time is required',
                                          validate: (value) => {
                                            const dayStartTime = watch(
                                              `dayTimes.${day}.startTime`,
                                            );
                                            if (!dayStartTime || !value)
                                              return true;
                                            return (
                                              isValidTimeRange(
                                                dayStartTime,
                                                value,
                                              ) ||
                                              'End time must be after start time'
                                            );
                                          },
                                        }}
                                        render={({ field }) => (
                                          <TimePickerDropdown
                                            value={field.value}
                                            onChange={(date) => {
                                              field.onChange(date);
                                            }}
                                          />
                                        )}
                                      />
                                    </div>
                                  </>
                                ) : (
                                  <div className='flex-1 italic text-gray-500'></div>
                                )}
                              </div>
                            </div>
                          </div>
                          {index < DAYS.length - 1 && (
                            <div className='my-2 border-t border-border' />
                          )}
                        </div>
                      ))}

                      {errors.days && (
                        <span className='text-sm text-destructive'>
                          {errors.days.message}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div className='space-y-4'>
                  <h2 className='text-xl font-semibold'>Days</h2>
                  <EventDateSelectionSwitch
                    checked={watch('eventInfo.allowSpecificDatesSelection')}
                    onCheckedChange={(checked) => {
                      setValue(
                        'eventInfo.allowSpecificDatesSelection',
                        checked,
                      );
                    }}
                  />
                  <div className='flex flex-wrap gap-2'>
                    {DAYS.map((day) => (
                      <div key={day} className='flex items-center space-x-2'>
                        <Checkbox
                          id={day}
                          checked={watch('days').includes(day)}
                          onCheckedChange={(checked) => {
                            const currentDays = watch('days');
                            if (checked) {
                              setValue('days', [...currentDays, day]);
                            } else {
                              setValue(
                                'days',
                                currentDays.filter((d) => d !== day),
                              );
                            }
                          }}
                        />
                        <Label htmlFor={day}>{day}</Label>
                      </div>
                    ))}
                  </div>
                  {errors.days && (
                    <span className='mt-2 block text-sm font-medium text-destructive'>
                      {errors.days.message}
                    </span>
                  )}
                </div>

                <div className='flex flex-wrap gap-4'>
                  <div className='min-w-[min(100%,15.5rem)] flex-1 space-y-2'>
                    <Label>Start Time</Label>
                    <Controller
                      control={control}
                      name='times.startTime'
                      rules={{ required: 'Start time is required' }}
                      render={({ field }) => (
                        <TimePickerDropdown
                          value={field.value}
                          onChange={(date) => {
                            field.onChange(date);
                          }}
                        />
                      )}
                    />
                    {errors.times?.startTime && (
                      <span className='text-sm text-destructive'>
                        {errors.times.startTime.message}
                      </span>
                    )}
                  </div>
                  <div className='min-w-[min(100%,15.5rem)] flex-1 space-y-2'>
                    <Label>End Time</Label>
                    <Controller
                      control={control}
                      name='times.endTime'
                      rules={{
                        required: 'End time is required',
                        validate: (value) => {
                          const currentStartTime = watch('times.startTime');
                          if (!currentStartTime || !value) return true;
                          return (
                            isValidTimeRange(currentStartTime, value) ||
                            'End time must be after start time'
                          );
                        },
                      }}
                      render={({ field }) => (
                        <TimePickerDropdown
                          value={field.value}
                          onChange={(date) => {
                            field.onChange(date);
                          }}
                        />
                      )}
                    />
                    {errors.times?.endTime && (
                      <span className='text-sm text-destructive'>
                        {errors.times.endTime.message}
                      </span>
                    )}
                  </div>
                  <div className='min-w-[min(100%,15.5rem)] flex-1 space-y-2'>
                    <Label>Duration (minutes)</Label>
                    <Input
                      type='text'
                      {...register('times.duration')}
                      disabled
                      value={calculateDurationInMinutes(startTime, endTime)}
                      placeholder='MM'
                    />
                  </div>
                </div>
              </>
            )}

            <div className='space-y-4'>
              <h2 className='text-xl font-semibold'>Tuition Info</h2>
              <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                <div className='space-y-2'>
                  <Label>Tuition Fee</Label>
                  <Input
                    {...register('otherInfo.tuitionFee', {
                      required: 'Tuition fee is required',
                      min: { value: 0, message: 'Fee cannot be negative' },
                      pattern: {
                        value: /^\d+(\.\d{1,2})?$/,
                        message: 'Please enter a valid amount',
                      },
                    })}
                    type='number'
                    min={0}
                    step='0.01'
                    placeholder='0.00'
                  />
                  {errors.otherInfo?.tuitionFee && (
                    <span className='text-sm text-destructive'>
                      {errors.otherInfo.tuitionFee.message}
                    </span>
                  )}
                </div>
                <div className='space-y-2'>
                  <Label>Event Registration Fee</Label>
                  <Input
                    {...register('otherInfo.registrationFee', {
                      min: {
                        value: 0,
                        message: 'Registration fee cannot be negative',
                      },
                      valueAsNumber: true,
                    })}
                    type='number'
                    min={0}
                    step='1'
                    placeholder='0.00'
                  />
                  {errors.otherInfo?.registrationFee && (
                    <span className='text-sm text-destructive'>
                      {errors.otherInfo.registrationFee.message}
                    </span>
                  )}
                </div>

                <div className='space-y-2'>
                  <Label>Costume Fee</Label>
                  <Input
                    {...register('otherInfo.costumeFee', {
                      min: { value: 0, message: 'Fee cannot be negative' },
                      pattern: {
                        value: /^\d+(\.\d{1,2})?$/,
                        message: 'Please enter a valid amount',
                      },
                    })}
                    type='number'
                    min={0}
                    step='1'
                    placeholder='0.00'
                  />
                  {errors.otherInfo?.costumeFee && (
                    <span className='text-sm text-destructive'>
                      {errors.otherInfo.costumeFee.message}
                    </span>
                  )}
                </div>

                <div className='space-y-2'>
                  <Label>Tuition Billing Cycle</Label>
                  <Controller
                    control={control}
                    name='otherInfo.tuitionBillingCycle'
                    rules={{
                      required: 'Tuition billing cycle is required',
                    }}
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue placeholder='Select Billing Cycle' />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={TuitionBillingCycle.ONE_TIME}>
                            One Time
                          </SelectItem>
                          <SelectItem value='monthly'>Monthly</SelectItem>
                          <SelectItem value='weekly'>Weekly</SelectItem>
                          <SelectItem value='bi-weekly'>Bi-Weekly</SelectItem>
                          <SelectItem value={TuitionBillingCycle.EVERY_X_DAYS}>
                            Every X Days
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.otherInfo?.tuitionBillingCycle && (
                    <span className='text-sm text-destructive'>
                      {errors.otherInfo.tuitionBillingCycle.message}
                    </span>
                  )}
                </div>

                {watch('otherInfo.tuitionBillingCycle') === TuitionBillingCycle.EVERY_X_DAYS && (
                  <div className='space-y-2'>
                    <Label>Billing Frequency (Days)</Label>
                    <Input
                      {...register('otherInfo.billingFrequencyDays', {
                        required:
                          'Billing frequency is required for every X days cycle',
                        min: {
                          value: 1,
                          message: 'Billing frequency must be at least 1 day',
                        },
                        valueAsNumber: true,
                      })}
                      type='number'
                      min={1}
                      placeholder='Enter number of days (e.g. 10)'
                    />
                    {errors.otherInfo?.billingFrequencyDays && (
                      <span className='text-sm text-destructive'>
                        {errors.otherInfo.billingFrequencyDays.message}
                      </span>
                    )}
                  </div>
                )}

                {watch('otherInfo.tuitionBillingCycle') === 'monthly' && (
                  <div className='space-y-2'>
                    <Label>Billing Day</Label>
                    <Controller
                      control={control}
                      name='otherInfo.billingDay'
                      rules={{
                        required: 'Billing day is required',
                        min: {
                          value: 1,
                          message: 'Billing day must be at least 1',
                        },
                        max: {
                          value: 28,
                          message: 'Billing day must be at most 28',
                        },
                      }}
                      render={({ field }) => (
                        <Input
                          type='number'
                          min={1}
                          max={28}
                          placeholder='Enter billing day (1-28)'
                          {...field}
                          value={field.value || ''}
                          onChange={(e) => {
                            const val =
                              e.target.value === ''
                                ? ''
                                : parseInt(e.target.value, 10);
                            field.onChange(val);
                          }}
                        />
                      )}
                    />
                    {errors.otherInfo?.billingDay && (
                      <span className='text-sm text-destructive'>
                        {errors.otherInfo.billingDay.message}
                      </span>
                    )}
                  </div>
                )}

                {watch('otherInfo.tuitionBillingCycle') &&
                  watch('otherInfo.tuitionBillingCycle') !== 'hourly' &&
                  watch('otherInfo.tuitionBillingCycle') !== TuitionBillingCycle.ONE_TIME && (
                    <div className='flex items-center gap-2 col-span-2'>
                      <Switch
                        id='prorateTuition'
                        checked={studioProrationEnabled && watch('otherInfo.prorateTuition')}
                        onCheckedChange={(checked) => {
                          setValue('otherInfo.prorateTuition', checked);
                        }}
                        disabled={!studioProrationEnabled}
                      />
                      <Label htmlFor='prorateTuition' className={!studioProrationEnabled ? 'text-muted-foreground' : ''}>
                        Enable Proration
                      </Label>
                      {!studioProrationEnabled && (
                        <span className='text-xs text-muted-foreground'>
                          (Proration is disabled at studio level)
                        </span>
                      )}
                    </div>
                  )}
              </div>
            </div>

            <div className='space-y-4'>
              <h2 className='text-xl font-semibold'>Other Info</h2>

              <div className='space-y-2'>
                <Label>Tags</Label>
                <Controller
                  control={control}
                  name='otherInfo.tags'
                  render={({ field }) => (
                    <TagsSelector
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                {errors.otherInfo?.tags && (
                  <span className='text-sm text-destructive'>
                    {errors.otherInfo.tags.message}
                  </span>
                )}
              </div>

              <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                <div className='space-y-2'>
                  <Label>Group</Label>
                  <Controller
                    control={control}
                    name='otherInfo.group'
                    render={({ field }) => (
                      <GroupsSelector
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                  {errors.otherInfo?.group && (
                    <span className='text-sm text-destructive'>
                      {errors.otherInfo.group.message}
                    </span>
                  )}
                </div>

                <div className='space-y-2'>
                  <Label>Policy</Label>
                  <Controller
                    control={control}
                    name='otherInfo.policyGroup'
                    render={({ field }) => (
                      <PolicySelector
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                  {errors.otherInfo?.policyGroup && (
                    <span className='text-sm text-destructive'>
                      {errors.otherInfo.policyGroup.message}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <Button
              type='submit'
              className='w-full'
              disabled={mutation.isPending}
            >
              {mutation.isPending ? (
                <>
                  <Loader2 className='mr-2 size-4 animate-spin' />
                  Creating Event
                </>
              ) : (
                'Submit'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
